// src/utils/metricsEngine.js
// Metrics Engine (FlowTrak)
// - Keep canonical function names
// - ALSO export backwards-compatible aliases used by older pages

/* =========================
   Basics
========================= */
export function safeISO(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString ? s : s;
}

// NOTE: safeISO returns the original string if it parses.
// If you want guaranteed ISO output, use safeToISO below.
function safeToISO(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export function secondsBetween(startISO, endISO) {
  const a = safeToISO(startISO);
  const b = safeToISO(endISO);
  if (!a || !b) return 0;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
  const diff = tb - ta;
  return diff > 0 ? diff / 1000 : 0;
}

export function avgSeconds(values) {
  const nums = (values || []).map((x) => Number(x)).filter((n) => Number.isFinite(n));
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function avgMinutes(values) {
  return avgSeconds(values) / 60;
}

/* =========================
   Date helpers (LOCAL day)
========================= */
export function toISODate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// Back-compat alias name used by pages
export const toISODateKey = toISODate;

export function startOfDayLocalMs(dateKey) {
  // dateKey "YYYY-MM-DD" interpreted in local time
  const d = new Date(`${dateKey}T00:00:00`);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function endOfDayLocalMs(dateKey) {
  const d = new Date(`${dateKey}T23:59:59.999`);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Back-compat exports (older pages expect ISO strings)
export function startOfDayISO(dateKey) {
  const ms = startOfDayLocalMs(dateKey);
  return ms ? new Date(ms).toISOString() : "";
}
export function endOfDayISO(dateKey) {
  const ms = endOfDayLocalMs(dateKey);
  return ms ? new Date(ms).toISOString() : "";
}

export function inLocalDay(iso, dateKey) {
  const s = safeToISO(iso);
  if (!s) return false;
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return false;
  const a = startOfDayLocalMs(dateKey);
  const b = endOfDayLocalMs(dateKey);
  return t >= a && t <= b;
}

// Back-compat helper name used by Parts page imports
export function inDateRangeISO(iso, fromDateKey, toDateKey) {
  const s = safeToISO(iso);
  if (!s) return false;
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return false;

  const a = startOfDayLocalMs(fromDateKey);
  const b = endOfDayLocalMs(toDateKey);
  return t >= a && t <= b;
}

/* =========================
   RO normalization
========================= */
export function normalizeROArray(store) {
  const ros = Array.isArray(store?.repairOrders)
    ? store.repairOrders
    : Array.isArray(store?.ros)
    ? store.ros
    : [];
  return Array.isArray(ros) ? ros : [];
}

export function sortROsByCreatedDesc(ros) {
  const arr = Array.isArray(ros) ? [...ros] : [];
  arr.sort((a, b) => {
    const ta = new Date(getCreatedISO(a) || 0).getTime();
    const tb = new Date(getCreatedISO(b) || 0).getTime();
    return tb - ta;
  });
  return arr;
}

export function filterROsByQuery(ros, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return Array.isArray(ros) ? ros : [];
  const arr = Array.isArray(ros) ? ros : [];

  const s = (v) => String(v ?? "").toLowerCase();

  return arr.filter((r) => {
    return (
      s(r.roNumber).includes(q) ||
      s(r.tagNumber).includes(q) ||
      s(r.customerName).includes(q) ||
      s(r.vehicle).includes(q) ||
      s(r.advisorName || r.serviceAdvisorName || r.writerName).includes(q) ||
      s(r.assignedTechName).includes(q) ||
      s(r.transportType).includes(q) ||
      s(r.workMix).includes(q) ||
      s(r.status).includes(q) ||
      s(r.lifecycle).includes(q) ||
      s(r.notes).includes(q) ||
      s(r.partsRequestType).includes(q) ||
      s(r.partsRequestNote).includes(q)
    );
  });
}

/* =========================
   Timestamp accessors
========================= */
export function getCreatedISO(ro) {
  return (
    safeToISO(ro?.timestamps?.createdAt) ||
    safeToISO(ro?.createdAt) ||
    ""
  );
}

export function getAssignedISO(ro) {
  return (
    safeToISO(ro?.timestamps?.assignedAt) ||
    safeToISO(ro?.assignedAt) ||
    ""
  );
}

export function getPromiseISO(ro) {
  return safeToISO(ro?.promiseTimeISO) || "";
}

export function getCompletedISO(ro) {
  return (
    safeToISO(ro?.timestamps?.completedAt) ||
    safeToISO(ro?.completedAt) ||
    ""
  );
}

// ✅ Needed by PartsManagerDashboard imports
export function getPartsActionedISO(ro) {
  return safeToISO(ro?.partsActionedAt) || "";
}

/* =========================
   Lifecycle / Status logic
========================= */
// Canonical "Like" functions
export function isArchivedLike(ro) {
  const st = String(ro?.status || "").toUpperCase();
  const lc = String(ro?.lifecycle || "").toUpperCase();
  return st === "ARCHIVED" || lc === "ARCHIVED";
}

export function isCompletedLike(ro) {
  const st = String(ro?.status || "").toUpperCase();
  const lc = String(ro?.lifecycle || "").toUpperCase();
  return st === "COMPLETED" || lc === "COMPLETED";
}

export function isAssignedLike(ro) {
  return !!String(ro?.assignedTechId || "").trim() || !!String(ro?.assignedTechName || "").trim();
}

export function isWaitingOnPartsLike(ro) {
  const st = String(ro?.status || "").toUpperCase();
  const lc = String(ro?.lifecycle || "").toUpperCase();
  return st === "WAITING_ON_PARTS" || lc === "WAITING_FOR_PARTS";
}

export function isOpenLike(ro) {
  const st = String(ro?.status || "").toUpperCase();
  const lc = String(ro?.lifecycle || "").toUpperCase();

  // Treat DISPATCH/OPEN as open-like
  if (st === "OPEN" || st === "DISPATCH") return true;

  // Lifecycle open states
  if (lc === "OPEN" || lc === "WAITING_FOR_ASSIGNMENT") return true;

  return false;
}

export function isOpenAsOf(ro, dateKey) {
  // if created on/before end of day and not completed before that day => open
  const created = getCreatedISO(ro);
  if (!created) return false;

  const createdMs = new Date(created).getTime();
  const endMs = endOfDayLocalMs(dateKey);
  if (!endMs || createdMs > endMs) return false;

  const completed = getCompletedISO(ro);
  if (!completed) return true;

  const completedMs = new Date(completed).getTime();
  return Number.isNaN(completedMs) ? true : completedMs > endMs;
}

// ✅ Back-compat alias names used by your Dispatch page (and others)
export const isArchivedRO = isArchivedLike;
export const isCompletedRO = isCompletedLike;

export function isWipRO(ro) {
  // WIP = not archived AND not completed
  return !isArchivedLike(ro) && !isCompletedLike(ro);
}

/* =========================
   Hours helper
========================= */
export function hoursBetweenISO(startISO, endISO) {
  const s = safeToISO(startISO);
  const e = safeToISO(endISO);
  if (!s || !e) return 0;
  const ms = new Date(e).getTime() - new Date(s).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return ms / 36e5;
}

/* =========================
   Date list helper
========================= */
export function lastNDatesISO(n = 14, endDate = new Date()) {
  const out = [];
  const d = endDate instanceof Date ? new Date(endDate) : new Date(endDate);
  if (Number.isNaN(d.getTime())) return out;

  for (let i = 0; i < n; i++) {
    const x = new Date(d);
    x.setDate(d.getDate() - i);
    out.push(toISODate(x));
  }
  return out.reverse();
}

/* =========================
   Advisor metrics (already in your engine)
   Keep these names if you already rely on them elsewhere.
========================= */
export function computeAdvisorList(ros, query) {
  return filterROsByQuery(sortROsByCreatedDesc(ros), query);
}

export function computeAdvisorDashboard(store, { query = "" } = {}) {
  const ros = normalizeROArray(store);
  const list = computeAdvisorList(ros, query);
  return { list, count: list.length };
}

/* =========================
   Operational metrics helper used by Manager Dashboard
========================= */
export function computeOperationalAsOf(ros, dateKey) {
  const arr = Array.isArray(ros) ? ros : [];
  const open = arr.filter((r) => isOpenAsOf(r, dateKey) && !isArchivedLike(r));
  const waitingParts = open.filter((r) => isWaitingOnPartsLike(r));
  const assigned = open.filter((r) => isAssignedLike(r));
  const unassigned = open.filter((r) => !isAssignedLike(r));
  return {
    dateKey,
    openCount: open.length,
    waitingPartsCount: waitingParts.length,
    assignedCount: assigned.length,
    unassignedCount: unassigned.length,
    openROs: open,
  };
}

/* =========================
   ✅ Manager KPI Range
   This is the missing export your Dashboard.js expects.
   Keep it simple + stable: return counts and key arrays.
========================= */
export function computeManagerKpiRange({ allRos = [], fromDateKey = "", toDateKey = "" } = {}) {
  const ros = Array.isArray(allRos) ? allRos : [];
  const fromOk = /^\d{4}-\d{2}-\d{2}$/.test(fromDateKey);
  const toOk = /^\d{4}-\d{2}-\d{2}$/.test(toDateKey);

  // If no range, just treat everything as “in range” (prevents crashes)
  const inRange = (iso) => {
    if (!fromOk || !toOk) return true;
    return inDateRangeISO(iso, fromDateKey, toDateKey);
  };

  const createdInRange = ros.filter((r) => inRange(getCreatedISO(r)));
  const completedInRange = ros.filter((r) => {
    const c = getCompletedISO(r);
    return c ? inRange(c) : false;
  });

  const openNow = ros.filter((r) => isWipRO(r));

  // lightweight averages (secondsBetween handles invalids)
  const cycleSecs = completedInRange
    .map((r) => secondsBetween(getCreatedISO(r), getCompletedISO(r)))
    .filter((x) => x > 0);

  const avgCycleSec = avgSeconds(cycleSecs);

  return {
    fromDateKey,
    toDateKey,
    createdCount: createdInRange.length,
    completedCount: completedInRange.length,
    openNowCount: openNow.length,
    avgCycleHours: avgCycleSec / 3600,
    createdInRange,
    completedInRange,
    openNow,
  };
}
